<template>
    <Notification
        v-if="group.notifications.length == 1"
        :notification="group.notifications[0]"
        :menuOpenId="menuOpenId"
        @closeMenu="$emit('closeMenu')"
        @openMenu="(id) => $emit('openMenu', id)"
    >
        <template v-slot:actions-menu>
            <button
                @click="
                    toggleNotificationAsRead(
                        group.notifications[0].id,
                        group.notifications[0].read_at == null
                    )
                "
                class="hover:bg-gray-300 hover:text-white px-1.5 py-px rounded-md font-medium opacity-100 hover:opacity-70 transition-opacity duration-text-hover"
                v-text="
                    group.notifications[0].read_at
                        ? $lang.get('Mark as unread')
                        : $lang.get('Mark as read')
                "
            />

            <button
                @click="removeNotification(group.notifications[0].id)"
                class="text-danger hover:bg-danger-hover hover:text-white px-1.5 py-px rounded-md font-medium transition-colors duration-text-hover"
                v-text="$lang.get('Remove notification')"
            />
        </template>
    </Notification>
    <div v-else>
        <!-- The entire notification is wrapped by the notificationContextMenu, this is for it to hover and appear correctly -->
        <NotificationContextMenu
            @close="$emit('closeMenu')"
            @open="$emit('openMenu', group.id)"
            :open="menuOpenId == group.id"
        >
            <template v-slot:notification>
                <div :id="group.id" :class="{ 'text-text-muted': !anyUnread }">
                    <div
                        @click="expanded = !expanded"
                        class="group rounded-button cursor-pointer flex group-hover:bg-gray-50 py-2 pl-2 pr-3 transition-colors duration-button-hover relative"
                    >
                        <div class="flex flex-grow items-center gap-x-3 pr-1">
                            <div class="size-16 flex-shrink-0">
                                <ModelImage
                                    :modelClass="group.source_type"
                                    :src="group.picture"
                                    class="rounded border"
                                    imageClasses="size-full"
                                    :typeId="group.type_id"
                                    :fileUrl="group.file"
                                />
                            </div>
                            <div class="text-base">
                                <span v-text="getGroupText()" />
                                <span
                                    class="font-semibold"
                                    v-text="' ' + group.name"
                                />
                                <button
                                    class="block underline decoration-transparent hover:decoration-current underline-offset-4 decoration-2 group-hover:decoration-current transition-colors duration-300"
                                    v-text="
                                        expanded
                                            ? $lang.get('Hide')
                                            : $lang.get('Show')
                                    "
                                />
                            </div>
                        </div>

                        <div class="flex items-center">
                            <div
                                :class="[
                                    { 'bg-blue-500': anyUnread },
                                    'w-2.5 h-2.5 rounded-full flex-shrink-0',
                                ]"
                            />
                        </div>
                    </div>
                </div>
            </template>
            <template v-slot:notification-actions>
                <button
                    @click="toggleNotificationsAsRead()"
                    class="hover:bg-gray-300 hover:text-white px-1.5 py-px rounded-md font-medium opacity-100 hover:opacity-70 transition-opacity duration-text-hover"
                    v-text="
                        anyUnread
                            ? $lang.get('Mark the group as read')
                            : $lang.get('Mark the group as unread')
                    "
                />

                <button
                    @click="removeNotifications()"
                    class="text-danger hover:bg-danger-hover hover:text-white px-1.5 py-px rounded-md font-medium transition-colors duration-text-hover"
                    v-text="$lang.get('Remove the notifications group')"
                />
            </template>
        </NotificationContextMenu>
        <VerticalSlideTransition :show="expanded">
            <div class="p-1">
                <div
                    class="ring ring-black/5 bg-gray-100/10 shadow-inner rounded"
                >
                    <TransitionGroup name="list-transition">
                        <Notification
                            v-for="notification in group.notifications"
                            :key="notification.id"
                            :notification="notification"
                            :menuOpenId="menuOpenId"
                            @closeMenu="$emit('closeMenu')"
                            @openMenu="(id) => $emit('openMenu', id)"
                        >
                            <template v-slot:actions-menu>
                                <button
                                    @click="
                                        toggleNotificationAsRead(
                                            notification.id,
                                            notification.read_at == null
                                        )
                                    "
                                    class="hover:bg-gray-300 hover:text-white px-1.5 py-px rounded-md font-medium opacity-100 hover:opacity-70 transition-opacity duration-text-hover"
                                    v-text="
                                        notification.read_at == null
                                            ? $lang.get('Mark as read')
                                            : $lang.get('Mark as unread')
                                    "
                                />

                                <button
                                    @click="removeNotification(notification.id)"
                                    class="text-danger hover:bg-danger-hover hover:text-white px-1.5 py-px rounded-md font-medium transition-colors duration-text-hover"
                                    v-text="$lang.get('Remove notification')"
                                />
                            </template>
                        </Notification>
                    </TransitionGroup>
                </div>
            </div>
        </VerticalSlideTransition>
    </div>
</template>

<script>
import Notification from '@/Components/Notifications/Notification.vue'
import ModelImage from '@/Components/ModelComponents/ModelImage.vue'
import VerticalSlideTransition from '@/Components/Transitions/VerticalSlideTransition.vue'
import NotificationContextMenu from '@/Components/Notifications/NotificationContextMenu.vue'
export default {
    emits: [
        'toggleNotificationsAsRead',
        'removeNotifications',
        'closeMenu',
        'openMenu',
    ],

    components: {
        Notification,
        ModelImage,
        VerticalSlideTransition,
        NotificationContextMenu,
    },

    props: {
        group: {
            type: Object,
            required: true,
        },
        menuOpenId: {
            type: [Number, String, null],
            required: true,
        },
    },

    data() {
        return {
            expanded: false,
        }
    },

    computed: {
        nbrOfUnread() {
            return this.group.notifications.reduce((acc, n) => {
                if (n.read_at == null) {
                    acc++
                }
                return acc
            }, 0)
        },

        anyUnread() {
            return this.nbrOfUnread > 0
        },
    },

    methods: {
        toggleNotificationAsRead(notificationId, read) {
            this.$emit('toggleNotificationsAsRead', {
                notificationIds: [notificationId],
                read: read,
            })
        },

        toggleNotificationsAsRead() {
            this.$emit('toggleNotificationsAsRead', {
                notificationIds: this.group.notifications.reduce((acc, n) => {
                    if (n.read_at == null) {
                        acc.push(n.id)
                    }
                    return acc
                }, []),
                read: this.anyUnread,
            })
        },

        removeNotification(notificationId) {
            this.$emit('removeNotifications', [notificationId])
        },

        removeNotifications() {
            this.$emit(
                'removeNotifications',
                this.group.notifications.map((n) => n.id)
            )
        },

        getGroupText() {
            if (this.nbrOfUnread > 0) {
                return this.$lang.get(
                    'You have :nbrOfUnread new :notification_types for',
                    {
                        nbrOfUnread: this.nbrOfUnread,
                        notification_types:
                            this.group.type == 'interaction'
                                ? this.$lang.get('interactions')
                                : this.$lang.get('notifications'),
                    }
                )
            }
            return this.$lang.get('You have :total :notification_types for', {
                total: this.group.notifications.length,
                notification_types:
                    this.group.type == 'interaction'
                        ? this.$lang.get('interactions')
                        : this.$lang.get('notifications'),
            })
        },
    },
}
</script>
